import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Filter, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';

import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageContainer, PageHeader } from '@/components/layout/PageContainer';
import { tableScrollMaxHeight } from '@/components/layout/layoutConstants';
import { useHeaderHeight } from '@/components/layout/useHeaderHeight';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

import { getAsset, listAssets, type AssetStatus } from './api';
import { readAssetIdFromSearch, withOpenAsset } from './assetPermalink';
import { BasketBar } from './components/basket/BasketBar';
import { BasketDrawer } from './components/basket/BasketDrawer';
import { takeOpenOnArrival, useBasket } from './components/basket/useBasket';
import { useBasketAssets } from './components/basket/useBasketAssets';
import { BriefingRowPanel } from './components/briefing/BriefingRowPanel';
import { useBriefingRows } from './components/briefing/useBriefingRows';
import { AssetDetailOverlay } from './components/detail/AssetDetailOverlay';
import { AssetFilterDrawer } from './components/filters/AssetFilterDrawer';
import {
  matchesBriefingRow,
  rowHasTier,
  useAssetFilters,
  useAssetFilterState,
} from './components/filters/useAssetFilters';
import { AssetGrid, AssetGridSkeleton } from './components/grid/AssetGrid';
import { SelectionToolbar } from './components/grid/SelectionToolbar';
import { HierarchicalAssetView } from './components/hierarchy/HierarchicalAssetView';
import { getVisibleOrdered, useHierarchicalAssets } from './components/hierarchy/assetHierarchy';
import { useAssetSelection } from './components/grid/useAssetSelection';
import { useAssetUrls, type AssetUrlLookup } from './components/grid/useAssetUrls';
import { olaAssetIds, useOlaTokens, type OlaTokenLookup } from './components/grid/useOlaTokens';
import { SidecarUploadModal } from './components/upload/SidecarUploadModal';
import { UploadBatchModal } from './components/upload/UploadBatchModal';
import { ASSET_TYPE_KEYS } from './constants/assetTypes';
import { isAssetExpired, isAssetExpiringSoon } from './constants/taxonomy';
import { readRowIdFromSearch, ROW_PARAM, stripRowParam, withSelectedRow } from './rowPermalink';
import { useAssetLibraryPermissions } from './useAssetLibraryPermissions';
import { useVocabulary } from './vocabulary/useVocabulary';

/**
 * Asset Library landing page.
 *
 * The tabs are a status filter: Staged is the working set awaiting metadata,
 * Published is the finished library. They are tabs rather than a dropdown
 * because the two are worked with at different times and the tab is the one
 * filter worth putting in the URL.
 *
 * Two things live in the URL, for different reasons. The tab is a filter worth
 * restoring on reload. The open asset is a permalink: it is how one person
 * sends another a specific asset, so it has to survive being pasted somewhere
 * and opened later by someone whose library has moved on. See
 * `assetPermalink.ts` for why the link carries the asset and nothing else.
 *
 * The grid has no side column. Metadata editing — one asset or a selection of
 * fifty — happens in the full-screen overlay, which leaves that column free for
 * the panel the Published tab is getting. Two panels flanking the grid, both
 * describing the same assets, is the layout this arrangement exists to prevent.
 *
 * Upload UI goes in `components/upload/`, the grid in `components/grid/`, the
 * full-screen detail view in `components/detail/`, and anything more than one
 * needs in `components/`.
 */

const TABS = [
  { key: 'staged', label: 'Staged' },
  { key: 'published', label: 'Published' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const TAB_PARAM = 'tab';

/** How long the search box waits for typing to settle before it searches. */
const SEARCH_DEBOUNCE_MS = 300;

const isBlankSearch = (value: string) => value.trim() === '';

/** One panel is rendered at a time, so every tab's aria-controls points here. */
const TAB_PANEL_ID = 'asset-library-tabpanel';

function isTabKey(value: string | null): value is TabKey {
  return TABS.some((tab) => tab.key === value);
}

/**
 * Read the requested tab from the URL so the view is linkable and survives a
 * reload. Other suite pages persist filters the same way, via URLSearchParams
 * rather than the router's search schema — this route declares none.
 *
 * Null when the URL names none: the default depends on the caller's
 * permissions, which are not known yet when this runs.
 */
function readTabFromUrl(): TabKey | null {
  const value = new URLSearchParams(window.location.search).get(TAB_PARAM);
  return isTabKey(value) ? value : null;
}

export function AssetLibraryPage() {
  const {
    permissions,
    isReady: isPermissionsReady,
    isError: isPermissionsError,
    refetch: refetchPermissions,
  } = useAssetLibraryPermissions();
  const canViewStaged = permissions.can_view_staged;
  // The briefing workflow — row panel, eligibility dimming, basket — belongs
  // to the roles that brief. Everyone else browses Published as a library.
  const canBrief = permissions.can_brief;

  // Staged is only a tab for roles that can see it; the backend refuses
  // `status=staged` to everyone else.
  const tabs = useMemo(
    () => (canViewStaged ? TABS : TABS.filter((tab) => tab.key !== 'staged')),
    [canViewStaged],
  );

  // What the URL or a click asked for, and what is actually shown. Derived
  // rather than corrected in an effect, so a link to `?tab=staged` opened by a
  // role without it lands on Published without ever requesting staged.
  const [requestedTab, setRequestedTab] = useState<TabKey | null>(readTabFromUrl);
  const activeTab: TabKey =
    requestedTab !== null && (requestedTab !== 'staged' || canViewStaged)
      ? requestedTab
      : canViewStaged
        ? 'staged'
        : 'published';

  // Kept unless the URL explicitly names Staged: without a tab param, whether
  // the row survives depends on the default tab, which the cleanup below
  // settles once the permissions are in.
  const [selectedRowId, setSelectedRowId] = useState<string | null>(() =>
    readTabFromUrl() === 'staged' ? null : readRowIdFromSearch(window.location.search),
  );

  // Clean up a stale ?row= param once the initial tab is known, if it isn't
  // Published — or if the caller cannot brief, which is the only use of a row.
  //
  // Only on real permissions. A failed request leaves every flag off, which
  // reads as "cannot brief" and would strip a shared link's row for good;
  // keyed on readiness rather than on loading, so a retry that succeeds still
  // gets to make the decision.
  useEffect(() => {
    if (!isPermissionsReady) return;
    if (activeTab === 'published' && canBrief) return;
    if (new URLSearchParams(window.location.search).has(ROW_PARAM)) {
      window.history.replaceState({}, '', stripRowParam(window.location.href));
    }
    setSelectedRowId(null);
    // Only when the permissions first arrive — tab changes already clear via
    // handleTabChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermissionsReady]);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isSidecarUploadOpen, setIsSidecarUploadOpen] = useState(false);
  const [openAssetId, setOpenAssetId] = useState<string | null>(() =>
    readAssetIdFromSearch(window.location.search),
  );
  // Bulk edit is state, not a URL param: a selection of twelve is not something
  // a link can reproduce for someone else, so putting it in the address bar
  // would promise a share that doesn't work.
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isGrouped, setIsGrouped] = useState(false);
  // Owned here rather than inside HierarchicalAssetView: orderedIds/totalVisible
  // need to filter out collapsed groups, so whatever computes those has to know
  // what's collapsed too. See getVisibleOrdered below.
  const [collapsedNameplates, setCollapsedNameplates] = useState<Set<string>>(() => new Set());
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(() => new Set());

  const handleToggleNameplateCollapse = useCallback((nameplateKey: string) => {
    setCollapsedNameplates((prev) => {
      const next = new Set(prev);
      if (next.has(nameplateKey)) {
        next.delete(nameplateKey);
      } else {
        next.add(nameplateKey);
      }
      return next;
    });
  }, []);

  const handleToggleTypeCollapse = useCallback((collapseKey: string) => {
    setCollapsedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(collapseKey)) {
        next.delete(collapseKey);
      } else {
        next.add(collapseKey);
      }
      return next;
    });
  }, []);

  const queryClient = useQueryClient();

  // Measured rather than hardcoded: the header grows once the upload button
  // and bulk-edit controls land, and a fixed value would push the grid
  // off-screen.
  const { headerRef, headerHeight } = useHeaderHeight();

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('asset-library-filters-collapsed');
      return saved !== 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('asset-library-filters-collapsed', String(!isFilterDrawerOpen));
    } catch {
      // Ignore localStorage errors
    }
  }, [isFilterDrawerOpen]);

  const filterState = useAssetFilterState();
  const { vocabulary } = useVocabulary();
  const { filters } = filterState;

  const canViewSourceImages = permissions.can_view_source_images;
  const visibleAssetTypes = useMemo(
    () =>
      canViewSourceImages ? ASSET_TYPE_KEYS : ASSET_TYPE_KEYS.filter((k) => k !== 'source_image'),
    [canViewSourceImages],
  );

  // Hidden kinds are dropped from what is sent, not from the filter state.
  // Filters persist, so one saved under a role that could see source images
  // would otherwise be a 403 on every load. Leaving it in the state keeps the
  // client-side filter honest: a filter of only source images still shows
  // "No matching assets" with a way to clear it, rather than every asset.
  const assetTypeParam = useMemo(() => {
    if (!filters.asset_type) return undefined;
    const kinds = filters.asset_type
      .split(',')
      .filter((kind) => (visibleAssetTypes as readonly string[]).includes(kind));
    return kinds.length > 0 ? kinds.join(',') : undefined;
  }, [filters.asset_type, visibleAssetTypes]);

  // Search is free text, so it reaches the request only once typing settles —
  // otherwise every keystroke is a backend search. Clearing it is not debounced:
  // there is nothing to wait for, and the full list should come straight back.
  const debouncedReferenceId = useDebouncedValue(filters.reference_id, SEARCH_DEBOUNCE_MS, {
    immediate: isBlankSearch,
  });
  // Trimmed because the backend ignores surrounding spaces: a trailing space
  // is the same search, not a new request.
  const referenceIdParam = debouncedReferenceId.trim() || undefined;

  // The tab is the status filter. Server-supported filters join these params
  // in the query key so a filter change refetches rather than being applied
  // client-side to a page the backend already truncated.
  const listParams = useMemo(
    () => ({
      status: activeTab as AssetStatus,
      nameplate: filters.nameplate || undefined,
      carline_code: filters.carline_code || undefined,
      business_unit: filters.business_unit || undefined,
      tier: filters.tier || undefined,
      asset_type: assetTypeParam,
      duration_seconds: filters.duration || undefined,
      film_format_size: filters.aspect_ratio || undefined,
      reference_id: referenceIdParam,
    }),
    [
      activeTab,
      filters.nameplate,
      filters.carline_code,
      filters.business_unit,
      filters.tier,
      assetTypeParam,
      filters.duration,
      filters.aspect_ratio,
      referenceIdParam,
    ],
  );

  // Held until the permissions arrive: the tab depends on them, and fetching
  // first would request the wrong tab — or one the backend refuses.
  const {
    data,
    isLoading: isAssetsLoading,
    isPlaceholderData: isShowingPreviousAssets,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      'asset-library',
      'assets',
      {
        ...listParams,
        // The same request answers differently with and without this
        // permission, so the two must not share a cache entry: after a role
        // downgrade the old answer would show until the refetch landed. The
        // staged dimension needs no entry of its own — `status` already
        // reflects it.
        can_view_source_images: canViewSourceImages,
      },
    ],
    queryFn: () => listAssets({ ...listParams, limit: 500 }),
    enabled: isPermissionsReady,
    // A filter change keeps the current grid on screen until the new answer
    // lands, rather than dropping back to the skeleton on every click. Only
    // within one tab and one permission set: Staged rows shown under the
    // Published tab would be offered Published actions.
    placeholderData: (previous, previousQuery) => {
      const previousKey = previousQuery?.queryKey[2] as
        { status?: string; can_view_source_images?: boolean } | undefined;
      return previousKey?.status === activeTab &&
        previousKey.can_view_source_images === canViewSourceImages
        ? previous
        : undefined;
    },
  });
  const isLoading = (!isPermissionsReady && !isPermissionsError) || isAssetsLoading;

  const rawAssets = useMemo(() => data?.assets ?? [], [data]);

  // ---------------------------------------- briefing row ↔ asset eligibility
  const {
    rows: briefingRows,
    isLoading: briefingRowsLoading,
    isError: briefingRowsFailed,
  } = useBriefingRows();

  const selectedRow = useMemo(
    () => (selectedRowId ? (briefingRows.find((r) => r.id === selectedRowId) ?? null) : null),
    [selectedRowId, briefingRows],
  );

  const { filteredAssets, activeFilterCount, handleFilterChange, handleClearFilters } =
    useAssetFilters(rawAssets, briefingRows, filterState, vocabulary);

  // While the previous response stands in for a filter change, keep showing
  // the rows that were on screen, not the old response re-filtered with the
  // new filters. Those can be disjoint — Mustang's rows filtered to Puma are
  // none — which would flash "No matching assets" until the answer lands.
  // Stored during render, not in an effect, so the first placeholder render
  // already has them.
  const [settledAssets, setSettledAssets] = useState(filteredAssets);
  if (!isShowingPreviousAssets && settledAssets !== filteredAssets) {
    setSettledAssets(filteredAssets);
  }
  const assets = isShowingPreviousAssets ? settledAssets : filteredAssets;

  // Banner tokens follow the same ownership rule as the signed URLs, and for a
  // sharper version of the same reason: the overlay plays the ad the grid was
  // already hovering, and re-minting on open would swap the iframe's src and
  // restart the animation from frame one.
  const olaIds = useMemo(() => olaAssetIds(assets), [assets]);
  const olaTokens = useOlaTokens(olaIds);

  const expiredIds = useMemo(() => {
    if (activeTab !== 'published') return new Set<string>();
    const set = new Set<string>();
    for (const asset of assets) {
      if (isAssetExpired(asset)) {
        set.add(asset.id);
      }
    }
    return set;
  }, [activeTab, assets]);

  const expiringSoonIds = useMemo(() => {
    const set = new Set<string>();
    for (const asset of assets) {
      if (isAssetExpiringSoon(asset)) {
        set.add(asset.id);
      }
    }
    return set;
  }, [assets]);

  /**
   * Whether the Published tab is in briefing mode: a row panel to pick from,
   * and the grid narrowed to what the picked row can use. Only for roles that
   * brief — for everyone else Published is a library to browse, and dimming
   * every card until a row they cannot pick is picked would lock them out of it.
   */
  const isBriefing = activeTab === 'published' && canBrief;

  /**
   * In briefing mode, the assets the selected row can use: they can go into
   * the basket, and come first in the grid.
   *  - null  → not briefing, or no row picked yet: nothing to match against,
   *            so nothing is ruled out. Dimming every card before a row is
   *            picked read as a disabled library to roles that can still act
   *            on it.
   *  - Set   → a row is picked. Ids outside the set cannot be added; empty
   *            means none can (no matches).
   * Expired assets are never eligible. What can be *selected*, and so what is
   * dimmed, is a separate question, answered by `disabledSelectionIds` below.
   */
  const eligibleIds = useMemo<Set<string> | null>(() => {
    if (!isBriefing || !selectedRow) return null;
    const ids = new Set<string>();
    for (const asset of assets) {
      if (expiredIds.has(asset.id)) continue;
      if (matchesBriefingRow(asset, selectedRow, vocabulary.format_types)) ids.add(asset.id);
    }
    return ids;
  }, [isBriefing, selectedRow, assets, expiredIds, vocabulary.format_types]);

  /**
   * The grid's order: with a row picked, what it can use comes first, so it is
   * not buried among hundreds of assets. Stable, so both halves keep their
   * usual order, and derived rather than stored, so unpicking the row restores
   * it. The grouped view does the same one level up, by putting the row's
   * nameplate section first.
   */
  const displayAssets = useMemo(() => {
    if (!eligibleIds) return assets;
    return [
      ...assets.filter((asset) => eligibleIds.has(asset.id)),
      ...assets.filter((asset) => !eligibleIds.has(asset.id)),
    ];
  }, [assets, eligibleIds]);

  const flatOrderedIds = useMemo(() => displayAssets.map((asset) => asset.id), [displayAssets]);
  const hierarchyPin = useMemo(
    () =>
      eligibleIds && selectedRow
        ? { nameplate: selectedRow.nameplate, matchingIds: eligibleIds }
        : null,
    [eligibleIds, selectedRow],
  );
  const hierarchicalData = useHierarchicalAssets(displayAssets, hierarchyPin);

  const orderedIds = isGrouped ? hierarchicalData.orderedIds : flatOrderedIds;
  const orderedAssets = isGrouped ? hierarchicalData.orderedAssets : displayAssets;

  // What's actually on screen: `orderedIds` above still includes assets
  // inside a collapsed nameplate/type group. Shift-click ranges and
  // "select all" have to be resolved against this instead, or they can
  // silently reach into a group the user just collapsed.
  const visibleOrderedIds = useMemo(() => {
    if (!isGrouped) return flatOrderedIds;
    return getVisibleOrdered(hierarchicalData.nameplateGroups, collapsedNameplates, collapsedTypes)
      .orderedIds;
  }, [
    isGrouped,
    flatOrderedIds,
    hierarchicalData.nameplateGroups,
    collapsedNameplates,
    collapsedTypes,
  ]);

  // What the toolbar offers for a selection on this tab. Named once, because
  // the selection rule below is built from the same answer.
  const canDeleteSelection = permissions.can_delete_assets;
  // Mirrors the overlay's read-only rule. A selection never spans tabs, so the
  // tab stands in for the assets' status.
  const canEditSelection =
    permissions.can_manage_assets && (activeTab !== 'published' || permissions.can_edit_published);
  // Staged assets never download, so on that tab the button could only ever
  // be greyed; hidden, like the overlay does.
  const canDownloadSelection = activeTab !== 'staged' && permissions.can_download_assets;

  /**
   * A picked row turns the page into briefing: the selection is for the
   * basket, whatever else the role can do. Without a row it is a library, and
   * a role that can delete, edit or download selects anything — a creative
   * agency briefs, so without this split it would only ever reach the source
   * images the picked row happens to match. One purpose at a time is also
   * what keeps "Add to basket" honest: a selection of everything, of which
   * the basket takes one, reads as twelve added.
   */
  const isRowPicked = eligibleIds !== null;
  const hasLibraryActions = canDeleteSelection || canEditSelection || canDownloadSelection;
  const canSelectFreely = hasLibraryActions && !isRowPicked;

  /**
   * A card is selectable only if a selection of it can be acted on:
   *  - a role that can delete, edit or download selects anything;
   *  - a briefer who can do none of those selects only what fits the picked
   *    row, since the basket is all their selection is for — and nothing
   *    before a row is picked;
   *  - anyone else (a viewer) selects nothing; a click opens the asset.
   * A card that cannot be selected shows no checkbox, and with a row picked
   * it is dimmed.
   */
  const disabledSelectionIds = useMemo(() => {
    if (canSelectFreely) return undefined;
    if (!eligibleIds) return new Set(orderedIds);
    const disabled = new Set<string>();
    for (const id of orderedIds) {
      if (!eligibleIds.has(id)) {
        disabled.add(id);
      }
    }
    return disabled;
  }, [eligibleIds, canSelectFreely, orderedIds]);

  const selection = useAssetSelection(visibleOrderedIds, disabledSelectionIds);

  // How many of the currently visible assets can actually be selected — the
  // denominator "select all" and "N selected (all on this page)" compare
  // against. Has to agree with what useAssetSelection above resolves against,
  // or collapsing a group (or an ineligible-on-Published asset) leaves
  // "select all" permanently short of complete.
  const totalVisible = useMemo(() => {
    if (!disabledSelectionIds) return visibleOrderedIds.length;
    let count = 0;
    for (const id of visibleOrderedIds) {
      if (!disabledSelectionIds.has(id)) count++;
    }
    return count;
  }, [visibleOrderedIds, disabledSelectionIds]);

  // Signed URLs are fetched here rather than inside the grid because the detail
  // overlay shows the same assets at full size. Signing in the grid would leave
  // the overlay to key its own query on the single open id, so every open would
  // re-sign an image the grid had already signed and still holds.
  //
  // Deliberately the full `orderedIds`, not `visibleOrderedIds`: expanding a
  // collapsed group should show thumbnails immediately, not trigger a fresh
  // signing round-trip.
  const urls = useAssetUrls(orderedIds);

  // Resolved from the live list rather than held alongside the ids, so the
  // drawer always shows current values — after a save invalidates the query,
  // the same selection re-derives from the refetched rows.
  const selectedAssets = useMemo(
    () => assets.filter((asset) => selection.selectedIds.has(asset.id)),
    [assets, selection.selectedIds],
  );

  // ------------------------------------------------------------- the basket
  //
  // Lives on the page rather than inside the panel because three things read
  // it: the cards (which are marked as added), the docked summary, and the
  // checkout drawer. Its lines are asset-and-row pairs, so it survives the row
  // selection changing — which is the point, since a basket is filled by
  // walking several rows in turn.
  const basket = useBasket();
  const [isBasketOpen, setIsBasketOpen] = useState(false);
  // Arriving from My Briefs' "Open in basket": the draft is already in the
  // basket (session storage), and the user came here to work on it. In an
  // effect rather than the initial state, so the one-time flag is read once
  // even when development mode mounts the page twice.
  useEffect(() => {
    if (takeOpenOnArrival()) setIsBasketOpen(true);
  }, []);

  // The basket resolves its own assets rather than reading the tab's page: it
  // is filled on Published and survives both a switch to Staged and a reload,
  // neither of which leaves that page holding what it refers to.
  const basketAssets = useBasketAssets(basket, assets, urls);

  // Depends on the callback rather than the basket object: the hook returns a
  // fresh object every render, so keying on it would recompute this on every
  // keystroke elsewhere on the page.
  const { assetIdsForRow, toggle: toggleBasket, addMany: addManyToBasket } = basket;

  const basketIdsForRow = useMemo(
    () => (selectedRow ? assetIdsForRow(selectedRow.id) : new Set<string>()),
    [assetIdsForRow, selectedRow],
  );

  const handleToggleBasket = useCallback(
    (assetId: string) => {
      if (!selectedRow) return;
      toggleBasket(selectedRow.id, assetId);
    },
    [toggleBasket, selectedRow],
  );

  // The eligible, not-yet-added part of the selection. Computed here rather
  // than in the toolbar so the button's disabled state and what the click
  // actually adds can't disagree.
  const addableSelection = useMemo(() => {
    if (!selectedRow || eligibleIds === null) return [];
    return selectedAssets
      .filter((asset) => eligibleIds.has(asset.id) && !basketIdsForRow.has(asset.id))
      .map((asset) => asset.id);
  }, [selectedRow, eligibleIds, selectedAssets, basketIdsForRow]);

  const handleAddSelectionToBasket = useCallback(() => {
    if (!selectedRow || addableSelection.length === 0) return;
    addManyToBasket(selectedRow.id, addableSelection);
    selection.clear();
    toast.success(`Added ${addableSelection.length} to the basket for ${selectedRow.campaign_id}`);
  }, [addManyToBasket, selectedRow, addableSelection, selection]);

  const matchingAssetsCount = useMemo(() => {
    if (!isBriefing || !selectedRow) return 0;
    return assets.filter((asset) => matchesBriefingRow(asset, selectedRow, vocabulary.format_types))
      .length;
  }, [isBriefing, selectedRow, assets, vocabulary.format_types]);

  const hasExpiredMatches =
    selectedRow !== null && (eligibleIds?.size ?? 0) === 0 && matchingAssetsCount > 0;

  // Every media-plan row should carry a tier. One without matches nothing, and
  // is surfaced as an error in the toolbar and the console, not as "no matches".
  const rowMissingTier = isBriefing && selectedRow !== null && !rowHasTier(selectedRow);
  useEffect(() => {
    if (rowMissingTier && selectedRow) {
      console.error(`Briefing row ${selectedRow.id} (${selectedRow.campaign_id}) has no tier`);
    }
  }, [rowMissingTier, selectedRow]);

  const handleTabChange = useCallback(
    (key: TabKey) => {
      setRequestedTab(key);
      selection.clear();
      setSelectedRowId(null);

      const url = new URL(window.location.href);
      url.searchParams.set(TAB_PARAM, key);
      url.searchParams.delete(ROW_PARAM);
      window.history.replaceState({}, '', url);
    },
    [selection],
  );

  const handleRowSelect = useCallback(
    (id: string | null) => {
      setSelectedRowId(id);
      // Picking or unpicking a row switches between briefing and library, and
      // a selection made for one means nothing in the other. Said out loud:
      // thirty picks vanishing without a word looks like a bug.
      if (selection.count > 0) {
        toast(
          id
            ? 'Selection cleared — picking a row starts a brief'
            : 'Selection cleared — back to the library',
        );
      }
      selection.clear();
      window.history.replaceState({}, '', withSelectedRow(window.location.href, id));
    },
    [selection],
  );

  // ------------------------------------------------ the open asset (permalink)

  /**
   * Back closes the overlay instead of leaving the library.
   *
   * Opening pushes a history entry, so the browser's back gesture is already
   * pointed at the grid; this is what turns that entry back into state. Without
   * it, back would restore the previous URL and leave the overlay on screen,
   * which is the one thing worse than back not working at all.
   */
  useEffect(() => {
    // Arriving straight from a shared link, there is no grid entry behind the
    // overlay for back to return to, so the first back would leave the app
    // entirely. Splitting the single entry into grid-then-overlay gives back
    // the same meaning it has when the overlay was opened by clicking a card.
    const initial = readAssetIdFromSearch(window.location.search);
    if (initial) {
      const href = window.location.href;
      window.history.replaceState({}, '', withOpenAsset(href, null));
      window.history.pushState({}, '', withOpenAsset(href, initial));
    }

    const syncFromUrl = () => setOpenAssetId(readAssetIdFromSearch(window.location.search));
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, []);

  // Push, so back closes. Stepping between assets replaces instead — walking a
  // batch with the arrow keys must not bury the grid under fifty entries.
  const handleOpenAsset = useCallback((assetId: string) => {
    setOpenAssetId(assetId);
    window.history.pushState({}, '', withOpenAsset(window.location.href, assetId));
  }, []);

  const handleNavigateAsset = useCallback((assetId: string) => {
    setOpenAssetId(assetId);
    window.history.replaceState({}, '', withOpenAsset(window.location.href, assetId));
  }, []);

  const handleCloseAsset = useCallback(() => {
    setOpenAssetId(null);
    window.history.replaceState({}, '', withOpenAsset(window.location.href, null));
  }, []);

  const assetInList = useMemo(
    () => assets.find((asset) => asset.id === openAssetId) ?? null,
    [assets, openAssetId],
  );

  /**
   * A shared link can name an asset this page hasn't got: it may be on the
   * other tab, or past the end of the page. Resolved directly by id in that
   * case, which is the whole reason `GET /assets/{id}` exists — going through
   * the list would make a link work or not depending on where the asset
   * happened to fall in the recipient's first page.
   */
  const isOutOfList = openAssetId !== null && assetInList === null;

  const { data: fetchedAsset, isError: openAssetFailed } = useQuery({
    queryKey: ['asset-library', 'asset', openAssetId],
    queryFn: () => getAsset(openAssetId!),
    // Also held for the permissions: its answer picks the tab, and until they
    // arrive the tab shown is only a placeholder.
    enabled: isOutOfList && isPermissionsReady,
    // A 404 here means deleted or never existed. Retrying can't change that,
    // and each attempt delays the message telling the user so.
    retry: false,
  });

  // The list copy wins whenever there is one: it is the row the drawer and the
  // grid are editing, so preferring it keeps a save visible in the overlay
  // immediately rather than after the by-id query happens to be invalidated.
  const openAsset = assetInList ?? (isOutOfList ? (fetchedAsset ?? null) : null);

  // Out-of-list assets aren't in the page the grid signed, so they need their
  // own URL. Keyed on the single id, and only enabled in that case, so the
  // common path stays one signing call for the whole page.
  const fallbackIds = useMemo(
    () => (isOutOfList && openAssetId !== null ? [openAssetId] : []),
    [isOutOfList, openAssetId],
  );
  const fallbackUrls = useAssetUrls(fallbackIds);

  // One lookup for the overlay to read, so it doesn't have to know which of the
  // two queries a given asset's URL came from. `refresh` invalidates by key
  // prefix, so either hook's copy re-signs both.
  const overlayUrls = useMemo<AssetUrlLookup>(
    () => ({
      urlById: new Map([...urls.urlById, ...fallbackUrls.urlById]),
      thumbnailById: new Map([...urls.thumbnailById, ...fallbackUrls.thumbnailById]),
      missing: new Set([...urls.missing, ...fallbackUrls.missing]),
      isLoading: isOutOfList ? fallbackUrls.isLoading : urls.isLoading,
      refresh: urls.refresh,
    }),
    [urls, fallbackUrls, isOutOfList],
  );

  // Same two-source shape for banner tokens: an asset opened from a link isn't
  // in the page the grid minted for, so it needs its own token or the overlay
  // shows a still it cannot play.
  const fallbackOlaIds = useMemo(
    () => (openAsset !== null && isOutOfList ? olaAssetIds([openAsset]) : []),
    [openAsset, isOutOfList],
  );
  const fallbackOlaTokens = useOlaTokens(fallbackOlaIds);
  const overlayOlaTokens = useMemo<OlaTokenLookup>(
    () => ({
      tokenById: new Map([...olaTokens.tokenById, ...fallbackOlaTokens.tokenById]),
    }),
    [olaTokens, fallbackOlaTokens],
  );

  /**
   * Open the current selection: one asset gets its own view and its own URL,
   * more than one gets the contact sheet. Same overlay either way.
   */
  const handleEditSelection = useCallback(() => {
    if (selectedAssets.length === 1) {
      handleOpenAsset(selectedAssets[0].id);
    } else if (selectedAssets.length > 1) {
      setIsBulkEditOpen(true);
    }
  }, [selectedAssets, handleOpenAsset]);

  // Publishing and deleting both drop rows out of the list, and the selection
  // prunes itself to match. Left open, the overlay would be editing a selection
  // that no longer exists.
  //
  // Emptying closes it. Shrinking to one does not: a partial publish narrows
  // the selection to the rows the backend refused, and when exactly one was
  // refused, closing would take the user off the single row they still have to
  // fix. One member renders as the single-asset view, which is the right thing
  // to be looking at.
  useEffect(() => {
    if (isBulkEditOpen && selectedAssets.length === 0) setIsBulkEditOpen(false);
  }, [isBulkEditOpen, selectedAssets.length]);

  /**
   * Put the recipient on the tab that actually contains the asset.
   *
   * A link carries no tab — the sender's tab is not a fact about the asset, and
   * an asset published since the link was sent would open behind an empty
   * Staged grid. The asset's own status is the honest answer, and it also gives
   * the overlay a list to step through.
   *
   * Archived assets belong to no tab; they open on whatever tab is showing and
   * simply offer no prev/next.
   */
  useEffect(() => {
    if (!isOutOfList || fetchedAsset === undefined) return;
    if (!isTabKey(fetchedAsset.status) || fetchedAsset.status === activeTab) return;
    handleTabChange(fetchedAsset.status);
  }, [isOutOfList, fetchedAsset, activeTab, handleTabChange]);

  /**
   * A dead link has to say so. Left alone it renders as an overlay that never
   * arrives, which is indistinguishable from a slow one — and the asset param
   * stays in the URL, so reloading reproduces it forever.
   */
  useEffect(() => {
    if (!openAssetFailed) return;
    toast.error('That asset is not available. It may have been deleted.');
    handleCloseAsset();
  }, [openAssetFailed, handleCloseAsset]);

  /**
   * Newly confirmed assets land `staged`, so the list they belong to is the
   * Staged tab's. Switching to it on upload avoids the case that otherwise
   * reads as a failed upload: finishing a batch while viewing Published, and
   * seeing nothing appear.
   */
  const handleUploaded = useCallback(() => {
    if (activeTab !== 'staged') handleTabChange('staged');
    void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
    void queryClient.invalidateQueries({ queryKey: ['asset-library', 'campaigns'] });
  }, [activeTab, handleTabChange, queryClient]);

  return (
    <>
      <PageHeader>
        <div ref={headerRef}>
          <Breadcrumb
            actions={
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFilterDrawerOpen((prev) => !prev)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
                    isFilterDrawerOpen || activeFilterCount > 0
                      ? 'border-[#0014cc] bg-[#0014cc]/10 text-[#0014cc]'
                      : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                  }`}
                  aria-label="Toggle filters"
                  aria-expanded={isFilterDrawerOpen}
                >
                  <Filter className="h-3.5 w-3.5" />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="rounded-full bg-[#0014cc] px-1.5 py-0.2 text-[10px] font-bold text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                {activeTab === 'staged' && permissions.can_manage_assets && (
                  <button
                    type="button"
                    onClick={() => setIsSidecarUploadOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-700 transition-colors hover:border-slate-400"
                  >
                    <FileText className="h-3.5 w-3.5" strokeWidth={2} />
                    Upload with JSON
                  </button>
                )}
                {activeTab === 'staged' && permissions.can_manage_assets && (
                  <button
                    type="button"
                    onClick={() => setIsUploadOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3]"
                  >
                    <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                    Upload
                  </button>
                )}
              </div>
            }
          />
          <div className="px-4">
            <div className="flex items-end gap-0 border-b border-slate-200" role="tablist">
              {tabs.map(({ key, label }) => {
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    // Both tabs point at the one panel that exists. Only the
                    // active panel is rendered, so a per-tab id would leave the
                    // inactive tab referencing a missing element.
                    aria-controls={TAB_PANEL_ID}
                    onClick={() => handleTabChange(key)}
                    className={`-mb-px whitespace-nowrap border-b-2 px-4 pb-2.5 pt-1 text-sm font-medium transition-all ${
                      isActive
                        ? 'border-[#0014cc] text-[#0014cc]'
                        : 'border-transparent text-slate-400 hover:border-slate-300 hover:text-slate-600'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <AssetFilterDrawer
            isOpen={isFilterDrawerOpen}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            activeFilterCount={activeFilterCount}
            briefingRows={briefingRows}
            assetTypes={visibleAssetTypes}
          />
        </div>
      </PageHeader>

      <PageContainer>
        <div id={TAB_PANEL_ID} role="tabpanel">
          {isPermissionsError ? (
            // Said out loud rather than rendered as the most restricted role:
            // an admin quietly shown a read-only library has no way to tell
            // that anything went wrong.
            <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-red-200 bg-white">
              <div className="max-w-md text-center">
                <h2 className="text-[15.5px] font-bold text-slate-800">
                  Couldn’t load your permissions
                </h2>
                <p className="mt-2 text-[12.5px] leading-relaxed text-slate-500">
                  The asset library needs them to know what to show you. This is a loading problem,
                  not a change to your access.
                </p>
                <button
                  type="button"
                  onClick={refetchPermissions}
                  className="mt-3 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3]"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : isLoading || (isShowingPreviousAssets && assets.length === 0) ? (
            // The second case is a filter change from a view that was already
            // empty: its "No matching assets" says nothing about the filters
            // now being asked for, so it is not kept on screen meanwhile.
            <AssetGridSkeleton />
          ) : isError ? (
            // Distinct from the empty state on purpose: a failed request that
            // renders as "no assets yet" reads as a real empty library and
            // invites someone to re-upload assets that are already there.
            <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-red-200 bg-white">
              <div className="max-w-md text-center">
                <h2 className="text-[15.5px] font-bold text-slate-800">Couldn’t load assets</h2>
                <p className="mt-2 text-[12.5px] leading-relaxed text-slate-500">
                  The asset library didn’t respond. This is a loading problem, not an empty library.
                </p>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="mt-3 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3]"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : rawAssets.length === 0 ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white">
              <div className="max-w-md text-center">
                <h2 className="text-[15.5px] font-bold text-slate-800">
                  {activeTab === 'staged' ? 'No staged assets yet' : 'No published assets yet'}
                </h2>
                <p className="mt-2 text-[12.5px] leading-relaxed text-slate-500">
                  {activeTab === 'staged'
                    ? 'Uploaded assets appear here for tagging before they are published.'
                    : canViewStaged
                      ? 'Assets you publish from the Staged tab will appear here.'
                      : 'Assets will appear here once they are published.'}
                </p>
              </div>
            </div>
          ) : assets.length === 0 ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white">
              <div className="max-w-md text-center">
                <h2 className="text-[15.5px] font-bold text-slate-800">No matching assets</h2>
                <p className="mt-2 text-[12.5px] leading-relaxed text-slate-500">
                  No assets match the active filter criteria. Try adjusting or clearing your
                  filters.
                </p>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-700 hover:border-slate-400"
                >
                  <X className="h-3.5 w-3.5 text-slate-400" />
                  Clear all filters
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex" style={{ height: tableScrollMaxHeight(headerHeight) }}>
                <div className="min-w-0 flex-1">
                  <SelectionToolbar
                    selection={selection}
                    totalVisible={totalVisible}
                    selectedAssets={selectedAssets}
                    canPublish={activeTab === 'staged' && permissions.can_manage_assets}
                    canDelete={canDeleteSelection}
                    canDownload={canDownloadSelection}
                    canEdit={canEditSelection}
                    onEdit={handleEditSelection}
                    showBriefingHints={isBriefing}
                    isBriefingRow={isRowPicked}
                    needsRowToSelect={isBriefing && !hasLibraryActions}
                    canSelect={isBriefing || hasLibraryActions}
                    hasRowSelected={selectedRow !== null}
                    eligibleCount={eligibleIds?.size ?? 0}
                    onAddSelectionToBasket={selectedRow ? handleAddSelectionToBasket : undefined}
                    addableCount={addableSelection.length}
                    hasExpiredMatches={hasExpiredMatches}
                    rowMissingTier={rowMissingTier}
                    isGrouped={isGrouped}
                    onToggleGrouping={() => setIsGrouped((prev) => !prev)}
                  />
                  {/* Faded while the previous answer stands in for a filter
                      change in flight, so it doesn't read as the result. */}
                  <div
                    className={`overflow-y-auto transition-opacity ${
                      isShowingPreviousAssets ? 'opacity-60' : ''
                    }`}
                    style={{ height: '100%' }}
                    aria-busy={isShowingPreviousAssets}
                  >
                    {isGrouped ? (
                      <HierarchicalAssetView
                        nameplateGroups={hierarchicalData.nameplateGroups}
                        urls={urls}
                        olaTokens={olaTokens}
                        isSelected={selection.isSelected}
                        isSelectable={selection.isSelectable}
                        onSelect={selection.select}
                        onOpen={handleOpenAsset}
                        eligibleIds={eligibleIds}
                        onToggleBasket={selectedRow ? handleToggleBasket : undefined}
                        basketIds={basketIdsForRow}
                        expiredIds={expiredIds}
                        expiringSoonIds={expiringSoonIds}
                        collapsedNameplates={collapsedNameplates}
                        collapsedTypes={collapsedTypes}
                        onToggleNameplateCollapse={handleToggleNameplateCollapse}
                        onToggleTypeCollapse={handleToggleTypeCollapse}
                      />
                    ) : (
                      <AssetGrid
                        assets={displayAssets}
                        urls={urls}
                        olaTokens={olaTokens}
                        isSelected={selection.isSelected}
                        isSelectable={selection.isSelectable}
                        onSelect={selection.select}
                        onOpen={handleOpenAsset}
                        eligibleIds={eligibleIds}
                        onToggleBasket={selectedRow ? handleToggleBasket : undefined}
                        basketIds={basketIdsForRow}
                        expiredIds={expiredIds}
                        expiringSoonIds={expiringSoonIds}
                      />
                    )}
                  </div>
                </div>
                {isBriefing && (
                  <BriefingRowPanel selectedRowId={selectedRowId} onRowSelect={handleRowSelect} />
                )}
              </div>
            </>
          )}
        </div>
      </PageContainer>

      {isUploadOpen && (
        <UploadBatchModal onClose={() => setIsUploadOpen(false)} onUploaded={handleUploaded} />
      )}
      {isSidecarUploadOpen && (
        <SidecarUploadModal
          onClose={() => setIsSidecarUploadOpen(false)}
          onUploaded={handleUploaded}
        />
      )}

      {/* Rendered on both tabs, not just Published. The basket outlives a tab
          change, and a summary that disappears when you go looking at Staged
          reads as a basket that emptied itself. Only for roles that brief:
          checking out a basket submits a brief. */}
      {canBrief && (
        <BasketBar
          basket={basket}
          assetById={basketAssets.assetById}
          urls={basketAssets.urls}
          onReview={() => setIsBasketOpen(true)}
        />
      )}

      {canBrief && isBasketOpen && (
        <BasketDrawer
          basket={basket}
          rows={briefingRows}
          rowsLoading={briefingRowsLoading}
          rowsFailed={briefingRowsFailed}
          assetById={basketAssets.assetById}
          urls={basketAssets.urls}
          onClose={() => setIsBasketOpen(false)}
        />
      )}

      {/* Outside the tab panel because it isn't part of it: the overlay covers
          the viewport, and an asset opened from a link may not even belong to
          the tab rendered underneath.

          A single open asset wins over bulk edit. The two can only overlap by
          opening one asset from a multi-selection, and in that case the asset
          the user just asked for is the one they meant. */}
      {openAsset ? (
        <AssetDetailOverlay
          assets={[openAsset]}
          urls={overlayUrls}
          olaTokens={overlayOlaTokens}
          // The list behind it, so stepping follows what the user was looking
          // at. Empty for an out-of-list asset, which correctly offers no
          // prev/next rather than inventing an order it isn't part of.
          siblings={isOutOfList ? [] : orderedAssets}
          onNavigate={handleNavigateAsset}
          onClose={handleCloseAsset}
        />
      ) : (
        isBulkEditOpen &&
        selectedAssets.length > 0 && (
          <AssetDetailOverlay
            assets={selectedAssets}
            urls={overlayUrls}
            olaTokens={overlayOlaTokens}
            siblings={orderedAssets}
            onNavigate={handleNavigateAsset}
            onClose={() => setIsBulkEditOpen(false)}
            onNarrowSelection={selection.replace}
          />
        )
      )}
    </>
  );
}
